import { type JSX, useState } from 'react';

import { LauncherModal } from './LauncherModal.js';

export interface DnsPingModalProps {
  onClose: () => void;
  /** When set, footer shows a "← Back" link that pops one modal off
   *  the navigation stack instead of dismissing entirely. */
  onBack?: () => void;
}

interface DnsResult {
  hostname: string;
  ips: string[];
  /** DoH round-trip in milliseconds. */
  latencyMs: number;
}

interface FailureResult {
  hostname: string;
  message: string;
}

type ProbeResult =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'ok'; data: DnsResult }
  | { kind: 'error'; data: FailureResult };

const DOH_ENDPOINT = 'https://cloudflare-dns.com/dns-query';

/**
 * Browser-side DNS lookup + latency. We can't do raw ICMP from a WebView,
 * so "ping" here means timing a DNS-over-HTTPS query — the same kind of
 * resolve a user-agent does on every fresh fetch. That's a useful network
 * health signal: if DoH to Cloudflare resolves in <50ms, the device is
 * online and DNS is healthy.
 *
 * Production-on-device should swap this for the JS Agent API's network
 * probes (which CAN do real ICMP via the MobiControl Agent). For the demo,
 * DoH is what the browser supports without CORS pain.
 */
export function DnsPingModal({ onClose, onBack }: DnsPingModalProps): JSX.Element {
  const [hostname, setHostname] = useState('mobilauncher.com');
  const [result, setResult] = useState<ProbeResult>({ kind: 'idle' });

  async function run(): Promise<void> {
    const trimmed = hostname.trim();
    if (!trimmed) return;
    setResult({ kind: 'pending' });

    const t0 = performance.now();
    try {
      const url = `${DOH_ENDPOINT}?name=${encodeURIComponent(trimmed)}&type=A`;
      const res = await fetch(url, {
        headers: { accept: 'application/dns-json' },
      });
      if (!res.ok) {
        setResult({
          kind: 'error',
          data: { hostname: trimmed, message: `DoH HTTP ${String(res.status)}` },
        });
        return;
      }
      const json = (await res.json()) as { Answer?: { data: string; type: number }[] };
      const ips = (json.Answer ?? [])
        .filter((a) => a.type === 1) // A record
        .map((a) => a.data);
      if (ips.length === 0) {
        setResult({
          kind: 'error',
          data: { hostname: trimmed, message: 'No A records returned' },
        });
        return;
      }
      const latencyMs = Math.round(performance.now() - t0);
      setResult({ kind: 'ok', data: { hostname: trimmed, ips, latencyMs } });
    } catch (err) {
      setResult({
        kind: 'error',
        data: {
          hostname: trimmed,
          message: err instanceof Error ? err.message : String(err),
        },
      });
    }
  }

  return (
    <LauncherModal
      title="Network Check"
      onClose={onClose}
      testId="dns-ping-modal"
      {...(onBack && { onBack })}
    >
      <form
        className="dns-ping"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <label className="dns-ping__field">
          <span className="dns-ping__label">Host</span>
          <input
            type="text"
            className="dns-ping__input"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            placeholder="example.com"
            autoComplete="off"
            spellCheck={false}
            data-testid="dns-ping-input"
          />
        </label>
        <button
          type="submit"
          className="dns-ping__submit"
          disabled={result.kind === 'pending'}
          data-testid="dns-ping-submit"
        >
          {result.kind === 'pending' ? 'Resolving…' : 'Resolve'}
        </button>

        <div className="dns-ping__result" data-testid="dns-ping-result">
          {result.kind === 'idle' && (
            <p className="dns-ping__hint">
              Resolves the host via Cloudflare DNS-over-HTTPS and reports the
              round-trip latency. Real ICMP ping requires the JS Agent API
              (Phase 4.5+).
            </p>
          )}
          {result.kind === 'pending' && (
            <p className="dns-ping__hint">Querying Cloudflare DoH…</p>
          )}
          {result.kind === 'ok' && (
            <div className="dns-ping__ok">
              <Row label="Host" value={result.data.hostname} />
              <Row
                label="A records"
                value={result.data.ips.join('\n')}
                multiline
              />
              <Row label="DoH round-trip" value={`${String(result.data.latencyMs)} ms`} tone="ok" />
            </div>
          )}
          {result.kind === 'error' && (
            <div className="dns-ping__err">
              <Row label="Host" value={result.data.hostname} />
              <Row label="Error" value={result.data.message} tone="warn" />
            </div>
          )}
        </div>
      </form>
    </LauncherModal>
  );
}

function Row({
  label,
  value,
  tone,
  multiline,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
  multiline?: boolean;
}): JSX.Element {
  return (
    <div className="dns-ping__row">
      <span className="dns-ping__row-label">{label}</span>
      <span
        className={[
          'dns-ping__row-value',
          tone ? `dns-ping__row-value--${tone}` : '',
          multiline ? 'dns-ping__row-value--multiline' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </span>
    </div>
  );
}
